import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import { ICoupon } from '../../interfaces/coupon';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import { showToastNotification } from '../../utility/helper';
import InputField from '../../components/UI/Forms/InputField';
import ErrorMessage from '../../components/UI/Notifications/ErrorMessage';
import Button from '../../components/UI/Buttons/Button';
import moment from '../../utility/moment-timezone';

const usageLimitFields = [
    { name: 'maxTotalUsage', label: 'Max Total Usage', placeholder: 'Enter max total usage' },
    { name: 'maxUsagePerUser', label: 'Max Usage Per User', placeholder: 'Enter max usage per user' },
    { name: 'maxUsagePerDay', label: 'Max Usage Per Day', placeholder: 'Enter max usage per day' },
    { name: 'maxUsagePerUserPerDay', label: 'Max Usage Per User Per Day', placeholder: 'Enter max usage per user per day' },
];

const defaultUnlimitedFlags: Record<string, boolean> = {
    maxTotalUsage: true,
    maxUsagePerUser: true,
    maxUsagePerDay: true,
    maxUsagePerUserPerDay: true,
};

const AddCoupon: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({ mode: 'onBlur' });
    const [loadingButton, setLoadingButton] = useState<boolean>(false);
    const [unlimitedFlags, setUnlimitedFlags] = useState<Record<string, boolean>>({ ...defaultUnlimitedFlags });

    useEffect(() => {
        if (id) {
            fetchCouponDetails();
        }
    }, [id]);

    const fetchCouponDetails = async () => {
        try {
            const response = await callApi(API_URL_2 + API_ENDPOINT.order + 'coupons', { method: 'GET' });
            const coupon: ICoupon | undefined = response.find((c: ICoupon) => c.id === id);
            if (coupon) {
                setValue('code', coupon.code);
                setValue('description', coupon.description);
                setValue('type', coupon.type);
                setValue('discountValue', coupon.discountValue);
                setValue('maxDiscount', coupon.maxDiscount);
                setValue('startDate', moment(coupon.startDate).format("YYYY-MM-DD"));
                setValue('expirationDate', moment(coupon.expirationDate).format("YYYY-MM-DD"));
                setValue('minimumValue', coupon.minimumValue);

                // Populate usage limit fields
                const newFlags = { ...defaultUnlimitedFlags };
                usageLimitFields.forEach((field) => {
                    const val = (coupon as any)[field.name];
                    if (val !== null && val !== undefined) {
                        newFlags[field.name] = false;
                        setValue(field.name, val);
                    } else {
                        newFlags[field.name] = true;
                        setValue(field.name, '');
                    }
                });
                setUnlimitedFlags(newFlags);
            }
        } catch (error: any) {
            showToastNotification('danger', error.message);
        }
    };

    const handleUnlimitedToggle = (fieldName: string, checked: boolean) => {
        setUnlimitedFlags((prev) => ({ ...prev, [fieldName]: checked }));
        if (checked) {
            setValue(fieldName, '', { shouldValidate: true });
        }
    };

    const onSubmit = async (data: any) => {
        setLoadingButton(true);

        // Transform usage limit fields: unlimited → null
        const submitData = { ...data };
        usageLimitFields.forEach((field) => {
            if (unlimitedFlags[field.name]) {
                submitData[field.name] = null;
            } else {
                submitData[field.name] = Number(submitData[field.name]);
            }
        });

        try {
            if (id) {
                await callApi(API_URL_2 + API_ENDPOINT.order + `/coupons/${id}`, { method: 'PUT', data: submitData });
                showToastNotification('success', 'Coupon updated successfully');
            } else {
                await callApi(API_URL_2 + API_ENDPOINT.order + '/coupons', { method: 'POST', data: submitData });
                showToastNotification('success', 'Coupon added successfully');
            }

            reset();
            navigate('/coupons');
        } catch (error: any) {
            showToastNotification('danger', error.message);
        } finally {
            setLoadingButton(false);
        }
    };

    return (
        <Layout>
            <div className='d-flex justify-content-between align-items-start'>
                <h1 className="app-page-title">{id ? 'Edit Coupon' : 'Add Coupon'}</h1>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="app-card app-card-settings shadow-sm p-4 mb-4">
                    <div className="app-card-body">
                        <div className="row">
                            {FieldConfigration.map((field, index) => (
                                <div className="mb-3 col-md-6 col-12" key={index}>
                                    {field.type === 'radio' ? (
                                        <div>
                                            <label className="form-label d-block">{field.label}</label>
                                            {field.options?.map((opt) => (
                                                <div className="form-check form-check-inline" key={opt.value}>
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        id={`${field.name}-${opt.value}`}
                                                        value={opt.value}
                                                        {...register(field.name, field.validation)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`${field.name}-${opt.value}`}>
                                                        {opt.label}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <InputField
                                            type={field.type}
                                            name={field.name}
                                            label={field.label}
                                            placeholder={field.placeholder}
                                            register={register(field.name, field.validation)}
                                        />
                                    )}
                                    <ErrorMessage message={errors[field.name]?.message?.toString()} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Coupon Usage Limits Card */}
                <div className="app-card app-card-settings shadow-sm p-4 mb-4">
                    <h5 className="mb-3 fw-bold">Coupon Usage Limits</h5>
                    <div className="app-card-body">
                        <div className="row">
                            {usageLimitFields.map((field) => {
                                const isUnlimited = unlimitedFlags[field.name];
                                return (
                                    <div className="mb-3 col-md-6 col-12" key={field.name}>
                                        <InputField
                                            type="number"
                                            name={field.name}
                                            label={field.label}
                                            placeholder={field.placeholder}
                                            disabled={isUnlimited}
                                            register={register(field.name, {
                                                required: !isUnlimited ? `${field.label} is required` : false,
                                                validate: !isUnlimited
                                                    ? (value: any) => {
                                                        const num = Number(value);
                                                        if (!Number.isInteger(num) || num < 1) {
                                                            return 'Must be a positive integer';
                                                        }
                                                        return true;
                                                    }
                                                    : undefined,
                                            })}
                                            step="1"
                                        />
                                        <ErrorMessage message={errors[field.name]?.message?.toString()} />
                                        <div className="form-check mt-1">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`unlimited-${field.name}`}
                                                checked={isUnlimited}
                                                onChange={(e) => handleUnlimitedToggle(field.name, e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor={`unlimited-${field.name}`}>
                                                Unlimited
                                            </label>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="col-12 text-end mt-3">
                    <Button
                        loading={loadingButton}
                        className="btn btn-primary text-white px-5"
                        type="submit"
                        text={id ? 'Update' : 'Save'}
                    />
                </div>
            </form>
        </Layout>
    );
};

export default AddCoupon;

// const today = new Date().toISOString().split('T')[0]
const FieldConfigration = [
    {
        name: 'code',
        label: 'Code',
        type: 'text',
        placeholder: 'Enter coupon code',
        validation: { required: 'Code is required' },
    },
    {
        name: 'description',
        label: 'Description',
        type: 'text',
        placeholder: 'Enter description',
        validation: { required: 'Description is required' },
    },
    {
        name: 'type',
        label: 'Value Type',
        type: 'radio',
        options: [
            { label: 'Percentage', value: 'percentage' },
            { label: 'Flat', value: 'flat' },
        ],
        validation: { required: 'Value type is required' },
    },
    {
        name: 'discountValue',
        label: 'Discount Value',
        type: 'number',
        placeholder: 'Enter discount percentage',
        validation: {
            required: 'Discount percentage is required',
            validate: (value: number) => value > 0 || 'Discount percentage must be greater than 0',
        },
    },
    {
        name: 'maxDiscount',
        label: 'Max Discount',
        type: 'number',
        placeholder: 'Enter max discount',
        validation: {
            required: 'Max discount is required',
            validate: (value: number) => value > 0 || 'Max discount must be greater than 0',
        },
    },
    {
        name: 'startDate',
        label: 'Start Date',
        type: 'date',
        placeholder: 'Enter Start date',
        validation: {
            required: 'Start date is required',
        },
    },
    {
        name: 'expirationDate',
        label: 'Expiration Date',
        type: 'date',
        placeholder: 'Enter expiration date',
        validation: {
            required: 'Expiration date is required',
            validate: (value: string) => {
                const selectedDate = new Date(value);
                const currentDate = new Date();
                return selectedDate > currentDate || 'Expiration date must be a future date';
            },
        },
    },
    {
        name: 'minimumValue',
        label: 'Minimum Purchase Value',
        type: 'number',
        placeholder: 'Enter minimum value',
        validation: {
            required: 'Minimum purchase value is required',
            validate: (value: number) => value >= 0 || 'Minimum purchase value cannot be negative',
        },
    },
]
